import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
  PieChart, Pie,
} from "recharts";
import {
  PhoneCall, MessageCircle, MessageSquare, Ticket, Clock, CheckCircle2, ArrowDownLeft, ArrowUpRight,
} from "lucide-react";

const CH_COLORS = { call: "#3B82F6", whatsapp: "#22C55E", sms: "#A855F7" };

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const pie = Object.entries(data.by_channel).map(([k, v]) => ({ name: k, value: v, color: CH_COLORS[k] }));

  return (
    <div>
      <PageHeader eyebrow="Insights" title="Analytics & Reports"
        subtitle="Call & message volume, response time and resolution health"
        image="https://images.unsplash.com/photo-1580418827493-f2b22c0a76cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxtZWNjYSUyMGthYWJhJTIwcGlsZ3JpbXN8ZW58MHx8fHwxNzkwMzQ4MDIzfDA&ixlib=rb-4.1.0&q=85"
      />
      <div className="p-4 sm:p-8 space-y-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={MessageCircle} label="Total interactions" value={data.total_messages} sub={`${data.inbound} in · ${data.outbound} out`} />
          <Kpi icon={Ticket} label="Open + pending" value={data.tickets.open + data.tickets.pending} sub={`${data.total_tickets} total tickets`} />
          <Kpi icon={CheckCircle2} label="Resolved" value={data.resolved} sub="tickets closed" />
          <Kpi icon={Clock} label="Avg. first response" value={`${data.avg_response_minutes}m`} sub="across tickets" />
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <Card className="p-5 bg-card/40 border-border/60 lg:col-span-2">
            <p className="eyebrow text-gold mb-4">7-Day Interaction Volume</p>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.volume_7d}>
                <XAxis dataKey="day" stroke="#7a8f86" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#7a8f86" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#05261D", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, color: "#fff" }} cursor={{ fill: "rgba(212,175,55,0.08)" }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {data.volume_7d.map((_, i) => <Cell key={i} fill="#D4AF37" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-5 bg-card/40 border-border/60">
            <p className="eyebrow text-gold mb-4">Channel Mix</p>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                  {pie.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#05261D", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, color: "#fff" }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 mt-2">
              {[["call", PhoneCall], ["whatsapp", MessageCircle], ["sms", MessageSquare]].map(([ch, Icon]) => (
                <div key={ch} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 capitalize"><Icon className="w-3.5 h-3.5" style={{ color: CH_COLORS[ch] }} />{ch}</span>
                  <span className="font-mono">{data.by_channel[ch] || 0}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <TicketStat label="Open" value={data.tickets.open} color="text-blue-300" />
          <TicketStat label="Pending" value={data.tickets.pending} color="text-amber-300" />
          <TicketStat label="Resolved" value={data.tickets.resolved} color="text-emerald-300" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <DirCard icon={ArrowDownLeft} label="Inbound" value={data.inbound} color="text-emerald-300" />
          <DirCard icon={ArrowUpRight} label="Outbound" value={data.outbound} color="text-gold" />
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <Card className="p-4 bg-card/40 border-border/60 card-hover">
      <div className="flex items-center gap-2 text-gold mb-2"><Icon className="w-4 h-4" /><span className="eyebrow text-muted-foreground">{label}</span></div>
      <p className="text-3xl font-serif font-semibold text-white">{value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
    </Card>
  );
}
function TicketStat({ label, value, color }) {
  return (
    <Card className="p-4 bg-card/40 border-border/60 flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label} tickets</span>
      <span className={`text-2xl font-serif font-semibold ${color}`}>{value}</span>
    </Card>
  );
}
function DirCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-4 bg-card/40 border-border/60 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-primary/40 flex items-center justify-center"><Icon className={`w-5 h-5 ${color}`} /></div>
      <div><p className="eyebrow text-muted-foreground">{label} messages</p><p className={`text-2xl font-serif font-semibold ${color}`}>{value}</p></div>
    </Card>
  );
}
